import { Box, Stack, Typography, IconButton, Tooltip } from '@mui/material';
import { mdiFileDelimitedOutline, mdiDownload } from '@mdi/js';
import MdiIcon from '../mdi/MdiIcon';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import type { ChatFile } from './types';

export interface ChatFileListProps {
  files: ChatFile[];
  /** Called with the file the user asked for. */
  onDownload: (file: ChatFile) => void;
  /** Disabled while a download is running, so it cannot be started twice. */
  busy?: boolean;
}

/**
 * The files an assistant reply produced, offered for download.
 *
 * A file is the answer to a question like "give me the logs as a
 * spreadsheet", so it is shown next to the reply rather than buried in the
 * tool call panel: the panel is collapsed by default, and a download the user
 * has to go looking for is a download they do not make.
 */
export function ChatFileList({ files, onDownload, busy }: ChatFileListProps) {
  if (files.length === 0) return null;

  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      {files.map((file) => (
        <Box
          key={file.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1,
            minHeight: 44,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <MdiIcon d={mdiFileDelimitedOutline} size={16} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={file.filename}>
              {file.filename}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Trans>{file.row_count} row(s)</Trans>
            </Typography>
          </Box>
          <Tooltip title={t`Download`}>
            <IconButton
              size="small"
              aria-label={t`Download ${file.filename}`}
              disabled={busy}
              onClick={() => onDownload(file)}
            >
              <MdiIcon d={mdiDownload} size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Stack>
  );
}
